// Keycloak Service - Manual Implementation tanpa dependency keycloak-js
// Menggunakan OAuth2/OIDC Authorization Code Flow dengan PKCE

// Konfigurasi Keycloak
const KEYCLOAK_CONFIG = {
  url: 'https://sso.jogjaprov.go.id',
  realm: 'satriya',
  clientId: 'peladen',
  redirectUri: `${window.location.origin}/login-sso`
};

// State management
let isInitialized = false;
let currentCallback = null;
let callbackExecuted = false;
let tokenData = null;
let tokenRefreshTimer = null;

// Storage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'kc_access_token',
  REFRESH_TOKEN: 'kc_refresh_token',
  TOKEN_EXPIRY: 'kc_token_expiry',
  ID_TOKEN: 'kc_id_token',
  STATE: 'kc_state',
  CODE_VERIFIER: 'kc_code_verifier'
};

// Helper: Generate random string untuk PKCE
const generateRandomString = (length = 43) => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const values = new Uint8Array(length);
  crypto.getRandomValues(values);
  for (let i = 0; i < length; i += 1) {
    result += charset[values[i] % charset.length];
  }
  return result;
};

// Helper: SHA256 hash untuk PKCE - return ArrayBuffer (raw bytes)
const sha256Bytes = async (plain) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return crypto.subtle.digest('SHA-256', data); // ArrayBuffer
};

// Helper: Base64 URL encode dari ArrayBuffer (raw bytes)
const base64UrlEncodeFromArrayBuffer = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

// Helper: Parse JWT token
const parseJwt = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Keycloak: Failed to parse JWT:', error);
    return null;
  }
};

// Helper: Cek apakah token masih valid
const isTokenValid = () => {
  const token = getStoredToken(STORAGE_KEYS.ACCESS_TOKEN);
  if (!token) return false;

  const parsed = parseJwt(token);
  if (!parsed || !parsed.exp) return false;

  return parsed.exp * 1000 > Date.now() + 30000;
};

// Helper: Simpan token ke storage
const saveToken = (accessToken, refreshToken, idToken) => {
  if (accessToken) {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    const parsed = parseJwt(accessToken);
    if (parsed && parsed.exp) {
      localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, parsed.exp.toString());
    }
  }
  if (refreshToken) {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  }
  if (idToken) {
    localStorage.setItem(STORAGE_KEYS.ID_TOKEN, idToken);
  }
  tokenData = {
    token: accessToken,
    refreshToken,
    idToken,
    parsed: accessToken ? parseJwt(accessToken) : null
  };
};

// Helper: Ambil token dari storage
const getStoredToken = (key) => localStorage.getItem(key);

// Helper: Clear semua token
const clearTokens = () => {
  Object.values(STORAGE_KEYS).forEach((key) => {
    localStorage.removeItem(key);
  });
  tokenData = null;
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
    tokenRefreshTimer = null;
  }
};

// Helper: Setup token refresh timer
const setupTokenRefresh = () => {
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer);
  }

  const token = getStoredToken(STORAGE_KEYS.ACCESS_TOKEN);
  if (!token) return;

  const parsed = parseJwt(token);
  if (!parsed || !parsed.exp) return;

  const expiryTime = parsed.exp * 1000;
  const refreshTime = expiryTime - Date.now() - 30000;

  if (refreshTime > 0) {
    tokenRefreshTimer = setTimeout(() => {
      updateToken().catch((error) => {
        console.error('Keycloak: Auto token refresh failed:', error);
      });
    }, refreshTime);
  }
};

// Exchange authorization code untuk token
const exchangeCodeForToken = async (code, codeVerifier) => {
  const tokenEndpoint = `${KEYCLOAK_CONFIG.url}/realms/${KEYCLOAK_CONFIG.realm}/protocol/openid-connect/token`;

  const params = new URLSearchParams();
  params.append('grant_type', 'authorization_code');
  params.append('code', code);
  params.append('redirect_uri', KEYCLOAK_CONFIG.redirectUri);
  params.append('client_id', KEYCLOAK_CONFIG.clientId);
  params.append('code_verifier', codeVerifier);

  try {
    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token exchange failed: ${response.status} ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Keycloak: Token exchange error:', error);
    throw error;
  }
};

// Refresh token
const updateToken = async () => {
  const refreshToken = getStoredToken(STORAGE_KEYS.REFRESH_TOKEN);
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const tokenEndpoint = `${KEYCLOAK_CONFIG.url}/realms/${KEYCLOAK_CONFIG.realm}/protocol/openid-connect/token`;

  const params = new URLSearchParams();
  params.append('grant_type', 'refresh_token');
  params.append('refresh_token', refreshToken);
  params.append('client_id', KEYCLOAK_CONFIG.clientId);

  try {
    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.status}`);
    }

    const data = await response.json();
    saveToken(data.access_token, data.refresh_token, data.id_token);
    setupTokenRefresh();
    return data.access_token;
  } catch (error) {
    console.error('Keycloak: Token refresh error:', error);
    clearTokens();
    throw error;
  }
};

// Handle callback dari Keycloak redirect
const handleCallback = async () => {
  let params = new URLSearchParams(window.location.search);
  if (!params.has('code') && window.location.hash) {
    params = new URLSearchParams(window.location.hash.substring(1));
  }

  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');

  if (error) throw new Error(`Authorization failed: ${error}`);
  if (!code || !state) return false;

  const storedState = localStorage.getItem(STORAGE_KEYS.STATE);

  if (!storedState || state !== storedState) {
    throw new Error('State mismatch - possible CSRF attack');
  }

  const codeVerifier = localStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
  if (!codeVerifier) throw new Error('Code verifier not found');

  const tokenResponse = await exchangeCodeForToken(code, codeVerifier);
  saveToken(tokenResponse.access_token, tokenResponse.refresh_token, tokenResponse.id_token);
  setupTokenRefresh();

  localStorage.removeItem(STORAGE_KEYS.STATE);
  localStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);

  window.history.replaceState(null, null, window.location.pathname);

  return true;
};

// Init Keycloak
const initKeycloak = async (onAuthenticatedCallback) => {
  if (
    isInitialized &&
    !window.location.search.includes('code=') &&
    !window.location.hash.includes('code=')
  ) {
    if (!callbackExecuted && onAuthenticatedCallback) {
      callbackExecuted = true;
      onAuthenticatedCallback();
    }
    return;
  }

  if (!onAuthenticatedCallback || typeof onAuthenticatedCallback !== 'function') {
    console.error('Keycloak: Invalid callback');
    return;
  }

  currentCallback = onAuthenticatedCallback;

  const urlHasCode =
    window.location.search.includes('code=') || window.location.hash.includes('code=');

  if (urlHasCode) {
    try {
      const authenticated = await handleCallback();
      if (authenticated && !callbackExecuted) {
        callbackExecuted = true;
        isInitialized = true;
        onAuthenticatedCallback();
      }
      return;
    } catch (error) {
      console.error('Keycloak: Callback failed:', error);
      onAuthenticatedCallback();
      return;
    }
  }

  if (isTokenValid()) {
    isInitialized = true;
    if (!callbackExecuted) {
      callbackExecuted = true;
      onAuthenticatedCallback();
    }
    return;
  }

  const refreshToken = getStoredToken(STORAGE_KEYS.REFRESH_TOKEN);
  if (refreshToken) {
    try {
      await updateToken();
      isInitialized = true;
      if (!callbackExecuted) {
        callbackExecuted = true;
        onAuthenticatedCallback();
      }
      return;
    } catch (e) {
      clearTokens();
    }
  }

  const hasKeycloakIdentityCookie = document.cookie
    .split(';')
    .some((c) => c.trim().startsWith('KEYCLOAK_IDENTITY'));

  if (hasKeycloakIdentityCookie) {
    login();
    return;
  }

  login();
};

// Login - redirect ke Keycloak
const login = async () => {
  const codeVerifier = generateRandomString();
  const hashBuffer = await sha256Bytes(codeVerifier);
  const codeChallenge = base64UrlEncodeFromArrayBuffer(hashBuffer);

  const state = generateRandomString();

  localStorage.setItem(STORAGE_KEYS.STATE, state);
  localStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);

  const authEndpoint = `${KEYCLOAK_CONFIG.url}/realms/${KEYCLOAK_CONFIG.realm}/protocol/openid-connect/auth`;
  const params = new URLSearchParams({
    client_id: KEYCLOAK_CONFIG.clientId,
    redirect_uri: KEYCLOAK_CONFIG.redirectUri,
    response_type: 'code',
    scope: 'openid profile email',
    state,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256'
  });

  window.location.href = `${authEndpoint}?${params.toString()}`;
};

// Logout
const logout = () => {
  clearTokens();
  isInitialized = false;
  currentCallback = null;
  callbackExecuted = false;

  const logoutEndpoint = `${KEYCLOAK_CONFIG.url}/realms/${KEYCLOAK_CONFIG.realm}/protocol/openid-connect/logout`;
  const params = new URLSearchParams({
    redirect_uri: window.location.origin
  });

  window.location.href = `${logoutEndpoint}?${params.toString()}`;
};

// Get token
const getToken = () => {
  if (isTokenValid()) {
    return getStoredToken(STORAGE_KEYS.ACCESS_TOKEN);
  }

  const refreshToken = getStoredToken(STORAGE_KEYS.REFRESH_TOKEN);
  if (refreshToken) {
    updateToken().catch(console.error);
    return getStoredToken(STORAGE_KEYS.ACCESS_TOKEN);
  }

  return null;
};

// Alias
const getAccessToken = () => getToken();
const getRefreshToken = () => getStoredToken(STORAGE_KEYS.REFRESH_TOKEN);

const getParsedToken = () => {
  const token = getToken();
  if (!token) return null;
  return parseJwt(token);
};

const isAuthenticated = () => isTokenValid();

// User profile
const loadUserProfile = async () => {
  const token = getToken();
  if (!token) throw new Error('No access token');

  const userInfoEndpoint = `${KEYCLOAK_CONFIG.url}/realms/${KEYCLOAK_CONFIG.realm}/protocol/openid-connect/userinfo`;

  const response = await fetch(userInfoEndpoint, {
    headers: { Authorization: `Bearer ${token}` }
  });

  if (!response.ok) {
    throw new Error(`User info failed: ${response.status}`);
  }

  return response.json();
};

// Initialize token dari storage
const initializeFromStorage = () => {
  const token = getStoredToken(STORAGE_KEYS.ACCESS_TOKEN);
  if (token && isTokenValid()) {
    const parsed = parseJwt(token);
    tokenData = {
      token,
      refreshToken: getStoredToken(STORAGE_KEYS.REFRESH_TOKEN),
      idToken: getStoredToken(STORAGE_KEYS.ID_TOKEN),
      parsed
    };
    setupTokenRefresh();
    isInitialized = true;
  }
};

initializeFromStorage();

// Export service
const KeycloakService = {
  initKeycloak,
  login,
  logout,
  isAuthenticated,
  getToken,
  getAccessToken,
  getRefreshToken,
  getParsedToken,
  updateToken,
  loadUserProfile,
  keycloak: {
    get authenticated() {
      return isAuthenticated();
    },
    get token() {
      return getToken();
    },
    get refreshToken() {
      return getRefreshToken();
    },
    get tokenParsed() {
      return getParsedToken();
    }
  }
};

export default KeycloakService;
